// Shared "Regime Edge" UI — consumes the additive per-regime Strike-Selection
// Lab cache (GET /api/regime-edge). Net-new elements: Scanner chip row,
// Screener Model-Alignment dot, Strategy-Analysis collapsible panel.
// Renders NOTHING when no fresh-shape cache exists (no crash, no broken UI).
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const REGIME_KEY = { "Quiet Range": "quiet", "Bullish Trend": "bullish", "Bearish Trend": "bearish", "Volatile": "volatile" };
const ORDER = [["quiet", "Quiet"], ["bullish", "Bullish"], ["bearish", "Bearish"], ["volatile", "Volatile"]];

// Per-regime chip status: ✓ EV>0 & ≥5cyc · ⚠ EV<0 & win>85 · ✗ EV<0 & win≤85 · — <3cyc
function chipState(cell) {
  if (!cell || (cell.cycles || 0) < 3) return { sym: "—", color: "var(--hoip-text-tertiary)", title: `insufficient data (${cell?.cycles || 0} cycles)` };
  if ((cell.cycles || 0) < 5) return { sym: "—", color: "var(--hoip-text-tertiary)", title: `only ${cell.cycles} cycles` };
  const ev = cell.ev_per_cycle || 0, win = cell.win_rate || 0;
  const t = `EV ${ev >= 0 ? "+" : ""}$${Math.round(ev)}/cycle · ${cell.cycles.toLocaleString()} cycles`;
  if (ev > 0) return { sym: "✓", color: "#10B981", title: t };
  if (win > 85) return { sym: "⚠", color: "#F59E0B", title: `${t} (marginal — high win, neg EV)` };
  return { sym: "✗", color: "#EF4444", title: t };
}

// Batch fetch + poll while any visible ticker is still computing.
export function useRegimeEdge(tickers) {
  const [edges, setEdges] = useState({});
  const pollRef = useRef(null);
  const key = (tickers || []).slice(0, 60).join(",");
  useEffect(() => {
    if (!key) return;
    let alive = true;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get("/regime-edge", { params: { tickers: key } });
        if (!alive) return data?.results || {};
        setEdges(data?.results || {});
        return data?.results || {};
      } catch { return {}; }
    };
    (async () => {
      const res = await fetchOnce();
      const anyComputing = Object.values(res).some(v => v?.status === "computing");
      if (anyComputing && !pollRef.current) {
        pollRef.current = setInterval(async () => {
          const r = await fetchOnce();
          if (!Object.values(r).some(v => v?.status === "computing")) {
            clearInterval(pollRef.current); pollRef.current = null;
          }
        }, 6000);
      }
    })();
    return () => { alive = false; if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [key]);
  return edges;
}

// Scanner card: "Edge in: [Quiet ✓] [Bullish ✓] [Bearish ⚠] [Volatile ✗]"
export function RegimeEdgeChips({ entry, compact }) {
  if (!entry) return null;
  if (entry.status === "computing") {
    return <span className="text-[9px] italic" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="regime-edge-computing">Computing regime edge…</span>;
  }
  if (entry.status !== "ready" || !entry.edge?.by_regime) return null;
  const br = entry.edge.by_regime;
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`regime-edge-chips-${entry.edge.ticker}`}>
      {!compact && <span className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>Edge in:</span>}
      {ORDER.map(([k, label]) => {
        const s = chipState(br[k]);
        return (
          <span key={k} className="text-[9px] font-mono-data px-1 py-0.5 rounded-sm" title={`${label}: ${s.title}`}
            data-testid={`regime-chip-${entry.edge.ticker}-${k}`}
            style={{ color: s.color, background: `${s.color === "var(--hoip-text-tertiary)" ? "transparent" : s.color}1A`, border: `1px solid ${s.color}33` }}>
            {label} {s.sym}
          </span>
        );
      })}
    </div>
  );
}

// Workbench section: "Strategy compatibility by regime" — self-contained chips
// for a single ticker (relocated here from the Scanner table row).
export function RegimeEdgeChipsForTicker({ ticker }) {
  const edges = useRegimeEdge(ticker ? [ticker] : []);
  const entry = edges[ticker];
  if (!entry || (entry.status !== "ready" && entry.status !== "computing")) return null;
  return (
    <div className="mt-3" data-testid="workbench-regime-compat">
      <div className="text-[11px] uppercase tracking-wide mb-1.5" style={{ color: "var(--hoip-text-tertiary)" }}>
        Strategy compatibility by regime
      </div>
      <RegimeEdgeChips entry={entry} />
    </div>
  );
}

// Screener Model-Alignment dot: green=current-regime +EV · amber=current-regime −EV · blue=surprising-edge
export function RegimeAlignmentDot({ entry, currentRegime }) {
  if (!entry || entry.status !== "ready" || !entry.edge?.by_regime || !currentRegime) return null;
  const edge = entry.edge;
  const k = REGIME_KEY[currentRegime];
  const cell = k && edge.by_regime[k];
  let tone = null, tip = "";
  if (edge.surprising_edge_regime === currentRegime) {
    tone = "#3B82F6";
    tip = `Unexpected edge: ${currentRegime} shows lower assignment risk than typical on ${edge.ticker}. Lab-verified.`;
  } else if (cell && (cell.cycles || 0) >= 5) {
    const ev = cell.ev_per_cycle || 0;
    if (ev > 0) { tone = "#10B981"; tip = `Regime-edge aligned: ${currentRegime} has historically positive EV on ${edge.ticker} (+$${Math.round(ev)}/cycle, ${cell.cycles.toLocaleString()} cycles)`; }
    else { tone = "#F59E0B"; tip = `Regime-edge caution: ${currentRegime} has historically negative EV on ${edge.ticker} (−$${Math.abs(Math.round(ev))}/cycle, ${cell.cycles.toLocaleString()} cycles). Consider reduced size or skip.`; }
  }
  if (!tone) return null;
  return <span className="inline-block w-2 h-2 rounded-full ml-1 align-middle" title={tip}
    data-testid={`regime-align-dot-${edge.ticker}`} style={{ background: tone, boxShadow: `0 0 4px ${tone}` }} />;
}

// Strategy Analysis: collapsible "Historical Regime Edge" panel.
export function RegimeEdgePanel({ ticker, currentRegime }) {
  const [open, setOpen] = useState(false);
  const [entry, setEntry] = useState(null);
  useEffect(() => {
    if (!ticker) return;
    let alive = true; let poll = null;
    const tick = async () => {
      try {
        const { data } = await api.get(`/regime-edge/${ticker}`);
        if (!alive) return data;
        setEntry(data);
        return data;
      } catch { return null; }
    };
    (async () => {
      const d = await tick();
      if (d?.status === "computing") poll = setInterval(async () => { const r = await tick(); if (r?.status !== "computing") { clearInterval(poll); poll = null; } }, 6000);
    })();
    return () => { alive = false; if (poll) clearInterval(poll); };
  }, [ticker]);

  if (!entry) return null;
  const computing = entry.status === "computing";
  const edge = entry.edge;
  const refresh = async () => { try { await api.post(`/regime-edge/${ticker}/refresh`); setEntry({ status: "computing", ticker }); } catch { /* noop */ } };

  return (
    <div className="border rounded-sm" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="regime-edge-panel">
      <button onClick={() => setOpen(o => !o)} data-testid="regime-edge-panel-toggle"
        className="w-full flex items-center justify-between px-3 py-2 text-left">
        <span className="text-[11px] font-semibold" style={{ color: "var(--hoip-text-primary)" }} title="HISTORICAL REGIME EDGE — Strike-Selection Lab aggregate: broader historical dataset spanning multiple DTE and strike buckets, sampled across cycles per regime. Distinct methodology from 'Regime Context' above (that panel is ticker × strategy × current DTE only). Numbers are expected to differ; use this panel for cross-strike sensitivity and Context for the specific ticker/DTE call.">Historical Regime Edge <span style={{ color: "var(--hoip-text-tertiary)" }}>· Strike-Selection Lab · multi-DTE/strike aggregate</span></span>
        <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{computing ? "computing…" : (open ? "▾" : "▸")}</span>
      </button>
      {open && (
        <div className="px-3 pb-3">
          {computing && <div className="text-[10px] italic py-2" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="regime-edge-panel-computing">Computing regime edge for {ticker}…</div>}
          {!computing && edge?.by_regime && (
            <>
              <table className="w-full">
                <thead><tr className="border-b" style={{ borderColor: "var(--hoip-divider)" }}>
                  {["Regime", "Win%", "Assign%", "Cycles"].map((h, i) => <th key={h} className={`text-[9px] py-1 ${i === 0 ? "text-left" : "text-right"}`} style={{ color: "var(--hoip-text-tertiary)" }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {ORDER.map(([k, label]) => {
                    const c = edge.by_regime[k];
                    const isNow = REGIME_KEY[currentRegime] === k;
                    if (!c || (c.cycles || 0) < 3) return <tr key={k} style={{ background: isNow ? "rgba(99,102,241,0.12)" : undefined }}><td className="text-left text-[10px] py-1" style={{ color: "var(--hoip-text-secondary)" }}>{label}{isNow ? " ◂ now" : ""}</td><td colSpan={3} className="text-right text-[10px] py-1 italic" style={{ color: "var(--hoip-text-tertiary)" }}>insufficient ({c?.cycles || 0})</td></tr>;
                    return <tr key={k} data-testid={`panel-regime-${k}`} style={{ background: isNow ? "rgba(99,102,241,0.12)" : undefined, fontFamily: "JetBrains Mono, monospace" }}>
                      <td className="text-left text-[10px] py-1" style={{ color: "var(--hoip-text-primary)" }}>{label}{isNow ? " ◂ now" : ""}</td>
                      <td className="text-right text-[10px] py-1">{c.win_rate?.toFixed(0)}%</td>
                      <td className="text-right text-[10px] py-1">{c.assign_pct?.toFixed(1)}%</td>
                      <td className="text-right text-[10px] py-1" style={{ color: "var(--hoip-text-tertiary)" }}>{c.cycles?.toLocaleString()}</td>
                    </tr>;
                  })}
                </tbody>
              </table>
              <div className="flex items-center justify-between mt-2 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                <span>Data from Strike-Selection Lab · {edge.last_updated ? new Date(edge.last_updated).toLocaleDateString() : "—"}</span>
                <a href={`/backtest?lab=philosophy&ticker=${ticker}`} className="underline" data-testid="regime-edge-lab-link" style={{ color: "#3B82F6" }} onClick={(e) => { e.preventDefault(); refresh(); window.location.href = `/backtest?lab=philosophy&ticker=${ticker}`; }}>Run / Refresh lab →</a>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
